import React, { useState, useEffect, useRef } from "react";
import styles from '../styles/BaksoTigerGame.module.css'

const tigerSize = 70;
const pipeWidth = 50;
const gap = 200;
const tigerHitboxReduction = 15;

interface Obstacle {
  x: number;
  topHeight: number;
  passed: boolean;
}

export default function BaksoTigerGame(): React.ReactElement {
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0); // Will be calculated
  const [containerWidth, setContainerWidth] = useState(0); // Will be calculated
  const [tigerY, setTigerY] = useState(200);
  const [obstacles, setObstacles] = useState<Obstacle[]>([]);
  const [score, setScore] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const velocity = useRef(0);
  const gravity = 0.5;
  const jumpStrength = -7;
  const speed = useRef(2);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const levelUpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Set up container dimensions on component mount and window resize
  useEffect(() => {
    const handleResize = () => {
      try {
        // Get real navbar height by finding the nav element
        const navElement = document.querySelector('nav, header, .header, .navbar');
        const navbarHeight = navElement ? navElement.getBoundingClientRect().height : 60;
        
        // Calculate available space
        const availableHeight = window.innerHeight - navbarHeight;
        const availableWidth = window.innerWidth;
        
        // Update state with new dimensions
        setContainerWidth(availableWidth);
        setContainerHeight(availableHeight);
        
        // If you have the gameContainerRef element, adjust its position
        if (gameContainerRef.current) {
          gameContainerRef.current.style.marginTop = `0px`;
        }
      } catch (error) {
        console.error("Error calculating game dimensions:", error);
        // Fallback to sensible defaults
        setContainerWidth(window.innerWidth);
        setContainerHeight(window.innerHeight - 60);
      }
    };

    // Initial setup
    handleResize();
    
    // Small delay to ensure DOM is fully loaded
    const timeoutId = setTimeout(handleResize, 100);
    
    // Add event listener for window resize
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      velocity.current += gravity;
      setTigerY((prev) => {
        const newY = prev + velocity.current;
        const maxY = containerHeight - tigerSize; // Use containerHeight
        return Math.max(Math.min(newY, maxY), 0);
      });
    }, 20);
    return () => clearInterval(interval);
  }, [containerHeight]);

  const handleJump = () => {
    if (isGameOver) {
      setObstacles([]);
      setScore(0);
      setIsGameOver(false);
      velocity.current = 0;
      setTigerY(200);
      speed.current = 2;
      return;
    }
    velocity.current = jumpStrength;
  };

  const showLevelUpNotification = () => {
    setShowLevelUp(true);
    
    if (levelUpTimer.current) {
      clearTimeout(levelUpTimer.current);
    }
    
    levelUpTimer.current = setTimeout(() => {
      setShowLevelUp(false);
    }, 2000);
  };

  useEffect(() => {
    const spawn = setInterval(() => {
      if (isGameOver || containerHeight === 0) return;
      
      const topHeight = Math.floor(Math.random() * (containerHeight - gap - 200)) + 100;
      
      setObstacles((prev) => [
        ...prev,
        { x: containerWidth, topHeight, passed: false },
      ]);
    }, 2000);
    return () => clearInterval(spawn);
  }, [isGameOver, containerHeight, containerWidth]);

  useEffect(() => {
    const mover = setInterval(() => {
      if (isGameOver) return;
      setObstacles((prev) => {
        const updated = prev
          .map((obs) => ({ ...obs, x: obs.x - speed.current }))
          .filter((obs) => obs.x + pipeWidth > 0);

        updated.forEach((obs) => {
          if (!obs.passed && obs.x + pipeWidth < 100) {
            obs.passed = true;
            setScore((prevScore) => {
              const newScore = prevScore + 1;
              if (newScore % 5 === 0) {
                speed.current *= 1.05;
                showLevelUpNotification();
              }
              return newScore;
            });
          }
        });

        return updated;
      });
    }, 20);
    return () => clearInterval(mover);
  }, [isGameOver]);

  useEffect(() => {
    obstacles.forEach((obs) => {
      const tigerLeft = 100 + tigerHitboxReduction;
      const tigerRight = 100 + tigerSize - tigerHitboxReduction;
      const tigerTop = tigerY + tigerHitboxReduction;
      const tigerBottom = tigerY + tigerSize - tigerHitboxReduction;
      
      const inX = tigerRight > obs.x && tigerLeft < obs.x + pipeWidth;
      const hitTop = tigerTop < obs.topHeight;
      const hitBottom = tigerBottom > obs.topHeight + gap;

      if (inX && (hitTop || hitBottom)) {
        setIsGameOver(true);
      }
    });
  }, [obstacles, tigerY]);

  useEffect(() => {
    return () => {
      if (levelUpTimer.current) {
        clearTimeout(levelUpTimer.current);
      }
    };
  }, []);

  // Don't render the game until we have valid dimensions
  if (containerHeight === 0 || containerWidth === 0) {
    return <div className={styles.loading}>Loading game...</div>;
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.gameContainerWrapper}>
        <div
          ref={gameContainerRef}
          className={`${styles.gameContainer} ${showLevelUp ? styles.levelUp : ""}`}
          onClick={handleJump}
          style={{ 
            width: `${containerWidth}px`, 
            height: `${containerHeight}px`,
          }}
        >
          <img src="/assets/jungle-bg.png" alt="Background" className={styles.background} />
          <img
            src="/assets/tiger2.png"
            alt="Tiger"
            className={styles.tiger}
            style={{
              top: `${tigerY}px`,
              left: "100px",
              width: `${tigerSize}px`,
              height: `${tigerSize}px`,
            }}
          />
          {obstacles.map((obs, i) => (
            <React.Fragment key={i}>
              <div
                className={styles.obstacle}
                style={{
                  left: `${obs.x}px`,
                  top: 0,
                  height: `${obs.topHeight}px`,
                  width: `${pipeWidth}px`,
                }}
              />
              <div
                className={`${styles.obstacle} ${styles.bottom}`}
                style={{
                  left: `${obs.x}px`,
                  top: `${obs.topHeight + gap}px`,
                  height: `${containerHeight - obs.topHeight - gap}px`,
                  width: `${pipeWidth}px`,
                }}
              />
            </React.Fragment>
          ))}
          <div className={styles.score}>Score: {score}</div>
          {showLevelUp && <div className={styles.levelUpMessage}>Level Up!</div>}
          {isGameOver && <div className={styles.gameOver}>Game Over! Tap to restart</div>}
          <button className={styles.jumpButton} onClick={handleJump}>
            JUMP
          </button>
        </div>
      </div>
    </div>
  );
}